import type { GameState } from './types'
import { validGame } from './game'
import { getAccessToken } from './auth'

const apiBase = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, '')
const tokenKey = 'lucky-estates-guest-token-v1'

let pendingToken:Promise<string|null>|null=null
export async function ensureGuestToken(): Promise<string | null> {
  if (!apiBase) return null
  const current = localStorage.getItem(tokenKey)
  if (current) return current
  if(pendingToken)return pendingToken
  pendingToken=(async()=>{
    const response=await fetch(`${apiBase}/session`,{method:'POST'})
    if(!response.ok)throw new Error('Cloud save session is unavailable.')
    const payload=await response.json() as {token:string}
    localStorage.setItem(tokenKey,payload.token)
    return payload.token
  })().finally(()=>{pendingToken=null})
  return pendingToken
}

export async function apiFetch(path:string,init:RequestInit={}):Promise<Response>{
  if(!apiBase)throw new Error('Cloud service is unavailable.')
  const accessToken=await getAccessToken(),token=accessToken??await ensureGuestToken()
  if(!token)throw new Error('A player session is unavailable.')
  const headers=new Headers(init.headers);headers.set('Authorization',`Bearer ${token}`)
  return fetch(`${apiBase}${accessToken?'/user':''}${path}`,{...init,headers})
}
export async function apiIdentity():Promise<{token:string;user:boolean}|null>{
  const accessToken=await getAccessToken();if(accessToken)return {token:accessToken,user:true}
  const guestToken=await ensureGuestToken();return guestToken?{token:guestToken,user:false}:null
}

export async function migrateGuestGame():Promise<boolean>{
  if(!apiBase)return false
  const accessToken=await getAccessToken();if(!accessToken)return false
  const guestToken=await ensureGuestToken();if(!guestToken)return false
  const response=await fetch(`${apiBase}/user/migrate`,{method:'POST',headers:{Authorization:`Bearer ${accessToken}`,'Content-Type':'application/json'},body:JSON.stringify({guestToken})})
  if(!response.ok)throw new Error('Your saved city could not be linked to the account.')
  return true
}

export async function loadCloudState(): Promise<GameState | null> {
  if (!apiBase) return null
  const response = await apiFetch('/state')
  if (response.status === 404) return null
  if (!response.ok) throw new Error('Cloud save could not be loaded.')
  const payload = await response.json() as {state:unknown}
  return validGame(payload.state) ? payload.state : null
}

export async function saveCloudState(state: GameState): Promise<void> {
  if (!apiBase) return
  const response = await apiFetch('/state',{method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify({state,revision:state.revision})})
  if (!response.ok) throw new Error('Cloud save could not be updated.')
}

export async function loadAdminStatus(): Promise<boolean> {
  if(!apiBase)return false
  const response=await apiFetch('/admin')
  if(!response.ok)return false
  return Boolean((await response.json() as {admin?:boolean}).admin)
}

export async function addAdminRolls(amount:number): Promise<GameState> {
  if(!apiBase)throw new Error('Admin access is unavailable.')
  const response=await apiFetch('/admin/rolls',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({amount})})
  const payload=await response.json() as {state?:unknown;message?:string}
  if(!response.ok||!validGame(payload.state))throw new Error(payload.message||'Rolls could not be added.')
  return payload.state
}

export const cloudConfigured = Boolean(apiBase)
